/**
 * L.5 stub + AIEditor Plan §5.2 canonical.
 *
 * Holds the user's current text selection inside the AIEditor (or any
 * future markdown viewer that wants to contribute selections to the
 * prompt). Ephemeral — cleared when the file closes, the scope
 * changes, or the user explicitly clears.
 *
 * Three consumers in the plan family:
 *   - `AIEditor` (5.1) — writes to the store via DOM selection capture.
 *   - `AIEditorPromptBox` (5.3) — reads to render the selection chip.
 *   - `ContextIndicator` (L.5) — reads to render the `· ✂ §N L…-…`
 *     adornment.
 *
 * L.5 ships this file as a tiny stub so the indicator can wire its
 * adornment; AIEditor phase 5.4 replaces the interior with the full
 * selection-capture pipeline (DOM line-mapping, versionHash locking,
 * attachment-payload helpers). Don't add business logic here until
 * phase 5.4 — this is deliberately minimal.
 *
 * See: ai-docs/lexide-aieditor-implementation-plan.md §5.2
 */

import { create } from "zustand";

/** Matches the shape declared in AIEditor plan Appendix A
 *  (`DocumentSelection`). Kept local here until phase 5.1 lands the
 *  authoritative `lexide/src/types/document-editor.ts` file. */
export interface DocumentSelection {
  docId: string;
  text: string;
  startLine: number;
  endLine: number;
  versionHash: string;
}

interface DocSelectionStore {
  selection: DocumentSelection | null;
  setSelection: (sel: DocumentSelection | null) => void;
  clearSelection: () => void;
}

export const useDocSelectionStore = create<DocSelectionStore>((set) => ({
  selection: null,
  setSelection: (sel) => set({ selection: sel }),
  clearSelection: () => set({ selection: null }),
}));

// DEV-only test accessor (mirrors `useWorkspaceFileStore.ts` pattern).
// The store is ephemeral, so E2E can't read via sessionStorage. The
// accessor is stripped from production bundles by Vite DCE when
// `import.meta.env.DEV` is `false`.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__dssStore", {
    value: useDocSelectionStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
